import { Icon } from 'shared/iconpack'

import { ReactNode, RefObject, useEffect, useRef, useState } from 'react'

import { Button } from 'shared/ui/Button'

import { palette } from 'shared/config/styles/palette'

import './TableSlider.css'

interface Props {
  children: ReactNode
  childrenRef: RefObject<HTMLDivElement>
  className?: string
}

const TableSlider = ({ children, childrenRef, className }: Props) => {
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)
  const [leftButtonPosition, setLeftButtonPosition] = useState<number | null>(
    null,
  )
  const isScrollingProgrammatically = useRef<boolean>(false)

  const updateButtonVisibility = (scrollLeft: number) => {
    const container = childrenRef.current

    if (container) {
      const isAtLeftEdge = scrollLeft === 0
      const isAtRightEdge =
        scrollLeft + container.offsetWidth >= container.scrollWidth

      setIsAtStart(isAtLeftEdge)
      setIsAtEnd(isAtRightEdge)
    }
  }

  const scrollTable = (direction: 'right' | 'left') => {
    if (childrenRef.current) {
      const container = childrenRef.current
      const scrollAmount = container.offsetWidth

      const newScrollLeft =
        direction === 'left'
          ? Math.max(0, container.scrollLeft - scrollAmount)
          : Math.min(
              container.scrollWidth - container.offsetWidth,
              container.scrollLeft + scrollAmount,
            )

      updateButtonVisibility(newScrollLeft)

      isScrollingProgrammatically.current = true

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      })

      container.onscrollend = () => {
        isScrollingProgrammatically.current = false
      }
    }
  }

  useEffect(() => {
    const updateLeftPosition = () => {
      if (childrenRef.current) {
        const rect = childrenRef.current.getBoundingClientRect()
        setLeftButtonPosition(rect.left - 10)
      }
    }

    const handleScroll = () => {
      if (!isScrollingProgrammatically.current && childrenRef.current) {
        updateButtonVisibility(childrenRef.current.scrollLeft)
      }
    }

    const observer = new ResizeObserver(() => {
      updateLeftPosition()
    })

    const mutationObserver = new MutationObserver(() => {
      updateLeftPosition()
      updateButtonVisibility(childrenRef.current?.scrollLeft || 0)
    })

    if (childrenRef.current) {
      observer.observe(childrenRef.current)
      mutationObserver.observe(childrenRef.current, {
        childList: true,
      })
      childrenRef.current.addEventListener('scroll', handleScroll)
    }
    updateLeftPosition()
    updateButtonVisibility(0)

    return () => {
      if (childrenRef.current) {
        observer.unobserve(childrenRef.current)
        mutationObserver.disconnect()
        childrenRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [childrenRef])

  return (
    <div className={`${className} childrenWrapper relative overflow-hidden`}>
      {children}
      {!isAtStart && (
        <Button
          className="fixed top-[70%] translate-y-[-50%] h-[80px] w-[45px] p-0 !bg-light_gray_5 rounded-r-[50px] border-none transition-none"
          onClick={() => scrollTable('left')}
          style={{ left: `${leftButtonPosition}px` }}
        >
          <Icon
            name="arrowLeft"
            size={35}
            color={palette.white}
          />
        </Button>
      )}
      {!isAtEnd && (
        <Button
          className="fixed top-[70%] translate-y-[-50%] right-[-8px] h-[80px] w-[45px] p-0 !bg-light_gray_5 rounded-l-[50px] border-none transition-none"
          onClick={() => scrollTable('right')}
        >
          <Icon
            name="arrowRight"
            size={35}
            color={palette.white}
          />
        </Button>
      )}
    </div>
  )
}

export default TableSlider
